import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as ScanTicketInstructionsIcon } from "assets/images/scan-passport-instructions.svg";
import { Box, Text } from "components";
import useCamera from "hooks/useCamera";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getTranslationsStyle } from "utils/arabisation.util";

import FixedOverlay from "../../components/FixedOverlay";

const FullScreenCamera = styled.div`
  top: 0;
  height: 100svh;
  width: 100%;
  position: fixed;
  overflow: hidden;
  z-index: ${(props) => props.theme.zIndex.modalClose};
  background-color: ${(props) => props.theme.colors.backgroundQuaternary};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const ShutterButton = styled.button`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: transparent;
  border: 4px solid rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::before {
    content: "";
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: white;
    transition: all 0.2s ease;
  }

  &:active::before {
    transform: scale(0.9);
  }
`;

function OCRScanner({ onScanComplete, onClose, pauseScanner, scanAnalytics }) {
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);

  useCamera(videoRef);

  const handleCaptureImage = async () => {
    if (
      pauseScanner.current ||
      !videoRef.current ||
      !canvasRef.current ||
      isCapturing
    ) {
      return;
    }

    const video = videoRef.current;

    // Make sure video is ready
    if (!video.videoWidth || !video.videoHeight) {
      console.error("Video not ready");
      return;
    }

    setIsCapturing(true);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Frame overlay dimensions (from FixedOverlay.jsx)
    const FRAME_LEFT_PERCENT = 4;
    const FRAME_TOP_PERCENT = 12;
    const FRAME_WIDTH_PERCENT = 93;
    const FRAME_HEIGHT_PERCENT = 45;

    // Calculate crop dimensions in pixels
    const cropX = (canvas.width * FRAME_LEFT_PERCENT) / 100;
    const cropY = (canvas.height * FRAME_TOP_PERCENT) / 100;
    const cropWidth = (canvas.width * FRAME_WIDTH_PERCENT) / 100;
    const cropHeight = (canvas.height * FRAME_HEIGHT_PERCENT) / 100;

    // Create a new canvas for the cropped image
    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = cropWidth;
    croppedCanvas.height = cropHeight;
    const croppedCtx = croppedCanvas.getContext("2d");

    // Draw the cropped region
    croppedCtx.drawImage(
      canvas,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    // Apply grayscale and enhance contrast
    const imageData = croppedCtx.getImageData(
      0,
      0,
      croppedCanvas.width,
      croppedCanvas.height
    );
    const pixels = imageData.data;

    // Convert to grayscale with enhanced contrast
    for (let i = 0; i < pixels.length; i += 4) {
      // Calculate grayscale value (weighted average for better perception)
      const gray =
        0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];

      // Apply strong contrast enhancement (factor of 1.8 for much sharper text)
      const contrastFactor = 1.8;
      const enhancedGray = Math.min(
        255,
        Math.max(0, (gray - 128) * contrastFactor + 128)
      );

      // Set RGB to the same grayscale value
      pixels[i] = enhancedGray; // Red
      pixels[i + 1] = enhancedGray; // Green
      pixels[i + 2] = enhancedGray; // Blue
      // Alpha (pixels[i + 3]) remains unchanged
    }

    // Put the processed image data back
    croppedCtx.putImageData(imageData, 0, 0);

    let processedCanvas = croppedCanvas;

    // Convert original canvas to blob
    canvas.toBlob(
      (originalBlob) => {
        if (!originalBlob) {
          console.error("Failed to create original image blob");
          setIsCapturing(false);
          return;
        }

        // Convert processed canvas to blob
        processedCanvas.toBlob(
          (processedBlob) => {
            if (!processedBlob) {
              console.error("Failed to create processed image blob");
              setIsCapturing(false);
              return;
            }

            // Mark analytics
            if (scanAnalytics && scanAnalytics.markFrameSent) {
              scanAnalytics.markFrameSent();
            }

            // Send both images to parent component immediately
            onScanComplete?.({
              originalImage: originalBlob,
              processedImage: processedBlob,
            });
          },
          "image/jpeg",
          0.95
        );
      },
      "image/jpeg",
      0.95
    );
  };

  useEffect(() => {
    if (scanAnalytics && scanAnalytics.markCameraOpened) {
      scanAnalytics.markCameraOpened();
    }
  }, [scanAnalytics]);

  return (
    <FullScreenCamera>
      <VideoContainer>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        <Box
          position="absolute"
          bottom="2vh"
          left="50%"
          w="65vw"
          d="flex"
          direction="column"
          gap="xs"
          justifyContent="center"
          alignItems="center"
          style={{ transform: "translateX(-50%)", zIndex: 1 }}
        >
          <ScanTicketInstructionsIcon
            style={{ transform: "scale(0.9) translateY(12px)" }}
          />
          <Text
            color="textQuaternary"
            fontSize="l"
            textAlign="center"
            style={getTranslationsStyle(i18n, { textAlign: "center" })}
          >
            {t("smart_gate.scanning_instructions")}
          </Text>
          <ShutterButton
            onClick={handleCaptureImage}
            disabled={isCapturing}
            aria-label="Capture passport image"
          />
        </Box>
        <FixedOverlay onClose={onClose} />
      </VideoContainer>
    </FullScreenCamera>
  );
}

export default OCRScanner;
